import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// organism — DWR builder/level-up sticky step nav. Top sticky sotto AppTopbar.
// Prev / center counter ("Step 04 / 10" + label) / Next (o Finalizza all'ultimo step).
// Pattern .step-nav / .step-nav__btn / .step-nav__center mockup
// (dev-workspace/mockups/.../project/styles.css righe 955-1040).
// Full-bleed orizzontale via negative margin per uscire dal padding del <MainGrid>.
// In PR 3 onNext sull'ultima sezione scrolla al ReviewPanel del consumer (decisione B1).
//
// **Decisione D1 PR 8**: `centerSubLabel?` opzionale sostituisce il default
// "Step XX / YY" sopra `currentLabel`. Usato dal level-up per mostrare
// "Step N / M · Sali a L{level}" (mockup levelup.jsx). Default invariato.
//
// **PR 9 fix UX**: `disableNextWhenIncomplete?: boolean` opt-in per
// disabilitare visivamente il button Avanti quando lo step corrente non
// è complete (cursor-not-allowed, no hover bg-ink). Default false
// preserva il behavior builder (Avanti è scroll-spy review-continuo,
// non gating). Level-up flow passa true (single-step paradigm: l'utente
// DEVE completare lo step corrente per proseguire).

interface StepNavProps {
  currentIndex: number;
  total: number;
  currentLabel: string;
  isCurrentComplete: boolean;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  /**
   * Stringa custom mostrata sopra `currentLabel` al posto del default
   * "Step XX / YY". Se assente, comportamento builder invariato.
   */
  centerSubLabel?: string;
  /**
   * Se `true`, il button Avanti (non-isLast) è disabled quando lo step
   * non è complete: cursor-not-allowed + nessun cambio colore on hover.
   * Default `false` (builder PR 3: il button Avanti è sempre attivo come
   * scroll-spy). Level-up flow passa `true` per il gating single-step.
   */
  disableNextWhenIncomplete?: boolean;
  /**
   * Label custom per il button Finalize (`isLast=true`). Default
   * `'Finalizza'` (builder). Level-up confirm passa
   * `'Conferma e applica L{N}'` (PR 10).
   */
  lastLabel?: string;
  /**
   * Label compatta per mobile (<sm) quando `lastLabel` è troppo lungo per
   * lo spazio centro del StepNav. Hub Mobile bundle round 2 (Francesco
   * 2026-05-19): "Conferma e applica L2" overflow su 375. Mobile usa
   * `lastLabelShort ?? lastLabel`.
   */
  lastLabelShort?: string;
  className?: string;
}

export function StepNav({
  currentIndex,
  total,
  currentLabel,
  isCurrentComplete,
  isLast,
  onPrev,
  onNext,
  centerSubLabel,
  disableNextWhenIncomplete = false,
  lastLabel = 'Finalizza',
  lastLabelShort,
  className,
}: StepNavProps) {
  // Prev NON è mai disabled: anche al primo step, click prev riporta in cima
  // alla sezione corrente (feedback PR 4 review). Se l'utente è già all'inizio
  // della prima sezione, il browser fa no-op (niente scroll).
  const stepNo = String(currentIndex + 1).padStart(2, '0');
  const totalNo = String(total).padStart(2, '0');
  const subLabel = centerSubLabel ?? `Step ${stepNo} / ${totalNo}`;

  return (
    <div
      data-last={isLast}
      className={cn(
        'bg-paper border-rule sticky top-14 z-10 mb-6 grid items-center gap-4 border-b',
        'mx-[calc(-1*clamp(1rem,4vw,3rem))] px-[clamp(1rem,4vw,3rem)] py-[0.8rem]',
        isLast ? 'grid-cols-[48px_1fr_auto]' : 'grid-cols-[48px_1fr_48px]',
        'max-[900px]:mx-[-1rem] max-[900px]:px-4',
        className,
      )}
    >
      <button
        type="button"
        onClick={onPrev}
        aria-label="Sezione precedente o inizio sezione corrente"
        className="border-ink text-ink grid h-12 w-12 place-items-center border-[1.5px] bg-transparent transition-[background-color,color] duration-150 hover:bg-[var(--color-ink)] hover:text-[var(--color-paper-bright)] active:scale-[0.96]"
      >
        <ChevronLeft className="h-5 w-5" strokeWidth={2.2} />
      </button>

      <CenterLabel subLabel={subLabel} currentLabel={currentLabel} />

      {isLast ? (
        <button
          type="button"
          onClick={onNext}
          disabled={!isCurrentComplete}
          aria-label={lastLabel}
          title={isCurrentComplete ? lastLabel : 'Completa tutte le sezioni prima di proseguire'}
          className={cn(
            'border-ink font-display inline-flex items-center gap-2 border-[1.5px] px-[1.1em] py-3 text-[0.85rem] font-bold tracking-[0.1em] whitespace-nowrap uppercase transition-[background-color,border-color,color] duration-150 active:not-disabled:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-25',
            isCurrentComplete
              ? 'bg-ink text-paper-bright hover:not-disabled:bg-accent-dwr hover:not-disabled:border-accent-dwr'
              : 'text-ink bg-transparent hover:not-disabled:bg-[var(--color-ink)] hover:not-disabled:text-[var(--color-paper-bright)]',
          )}
        >
          <span className={lastLabelShort ? 'hidden sm:inline' : undefined}>{lastLabel}</span>
          {lastLabelShort ? <span className="sm:hidden">{lastLabelShort}</span> : null}
          <ChevronRight className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden />
        </button>
      ) : (
        <NextButton
          isComplete={isCurrentComplete}
          disableWhenIncomplete={disableNextWhenIncomplete}
          onClick={onNext}
        />
      )}
    </div>
  );
}

// **FI-71**: estratto per ridurre complessità ciclomatica di StepNav.
// `currentLabel` vuoto skippa il render dello span: usato dal level-up
// confirm step dove il titolo grande della pagina già lo esprime
// ("Conferma il livello N") e il sub-titolo nel StepNav era ridondante.
function CenterLabel({ subLabel, currentLabel }: { subLabel: string; currentLabel: string }) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-[0.1rem] text-center">
      <span className="font-display text-ink-faint text-[0.7rem] tracking-[0.16em] uppercase">
        {subLabel}
      </span>
      {currentLabel ? (
        <span className="font-display text-[1.1rem] font-bold tracking-[0.05em] uppercase">
          {currentLabel}
        </span>
      ) : null}
    </div>
  );
}

function NextButton({
  isComplete,
  disableWhenIncomplete,
  onClick,
}: {
  isComplete: boolean;
  disableWhenIncomplete: boolean;
  onClick: () => void;
}) {
  const isDisabled = disableWhenIncomplete && !isComplete;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label="Sezione successiva"
      aria-disabled={isDisabled ? 'true' : undefined}
      title={isComplete ? 'Avanti' : 'Sezione non ancora completa'}
      className={cn(
        'border-ink grid h-12 w-12 place-items-center border-[1.5px] transition-[background-color,border-color,color] duration-150 active:not-disabled:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-30',
        nextButtonStateClasses(isComplete, disableWhenIncomplete),
      )}
    >
      <ChevronRight className="h-5 w-5" strokeWidth={2.2} />
    </button>
  );
}

function nextButtonStateClasses(isComplete: boolean, disableWhenIncomplete: boolean): string {
  if (isComplete) return 'bg-ink text-paper-bright hover:bg-accent-dwr hover:border-accent-dwr';
  if (disableWhenIncomplete) return 'text-ink bg-transparent';
  // Builder review-continuo (PR 3): hover bg-ink anche su incomplete = ok,
  // il button è scroll-spy non gating.
  return 'text-ink bg-transparent hover:bg-[var(--color-ink)] hover:text-[var(--color-paper-bright)]';
}
